/**
 * "Download PDF" — one control, five mount points.
 *
 * ⚠️ *SUPERSEDED: "four mount points".* `CreditNoteDetailPage` joined on
 * 2026-09-27 over `GET /credit-notes/{id}/pdf`. A credit note is gated
 * differently — its read carries no `xero` envelope yet, see
 * `canDownloadCreditNotePdf` — and, like an invoice, it has no mark-sent route.
 * Since 2026-10-02 every PDF route takes its module's EXPORT grant
 * (`quotes.export`, `invoices.export`, `credit-notes.export`) via
 * `requireGrant`; *SUPERSEDED: "Unlike the quote and invoice PDFs, the
 * credit-note route is gated on `credit-notes.read`".* A 403 is reachable on
 * all three (a grant can change under a live token); it falls through to the
 * server's own sentence like every other unrecognised refusal.
 *
 * `GET /quotes/{id}/pdf` and `GET /invoices/{id}/pdf` answer the document as
 * Xero renders it: the PDF that goes out for signature, and the one a customer
 * is invoiced against. The control is offered on `QuoteDetailPage`,
 * `InvoiceDetailPage` and as a row action on both list pages, in the desktop
 * table and the sub-`md` card list.
 *
 * ## Why one component rather than a button on each page
 *
 * Everything difficult here is *state per record*, not layout: an in-flight
 * guard, an abort on unmount, and a refusal that has to be worded from
 * `error.code` rather than from the status. Four copies would agree the day they
 * were written and drift on the first correction — and what would drift is
 * either whether the control is OFFERED at all, or what it says when Xero
 * cannot produce the document. `@/lib/document-pdf` owns the visibility test and
 * the wording; this owns the state.
 *
 * ## ⚠️ It renders NOTHING until the document is in Xero
 *
 * Owner's ruling: hidden, not disabled, on the precedent of the Xero `sync`
 * affordance — a button whose only possible outcome is an error is a button
 * nobody should be shown. `canDownloadPdf` is the test and reads the LINK ROW;
 * read its notes before changing the condition, because the obvious-looking
 * alternatives (`xeroQuoteNumber`, `xeroQuoteId`) are the ones that once had a
 * quote advertising another quote's document.
 *
 * **The gate is an optimisation, not a guarantee.** A link can be detached by
 * somebody else while this page sits open, so both 409s are still handled here.
 *
 * ## 🔴 A QUOTE OFFERS TWO DOWNLOADS, AND ONE OF THEM WRITES
 *
 * `POST /quotes/{id}/pdf/mark-sent` moves the Xero quote out of Draft and then
 * answers the PDF.
 *
 * 🔴 **CORRECTED 2026-09-16 BY MEASUREMENT.** *SUPERSEDED: "**Xero renders the
 * DRAFT watermark itself, from the quote's own status, at the moment it builds
 * the document** — so moving the status is the only way to obtain bytes a
 * customer can sign, and nothing done on this side substitutes for it."*
 *
 * **Xero puts no draft marking on a quote's PDF at all**, so the two paths hand
 * back the SAME bytes — QU-0540 measured 39,995 as a draft and 40,006 as Sent
 * forty-four seconds later. The DRAFT watermark is an INVOICE behaviour in
 * Xero. What the write buys is the STATUS, which is the same move a signature
 * request makes; it does not buy a different document.
 *
 * 🔴 **AND SINCE 2026-09-16 THAT MOVE IS IN BOTH SYSTEMS** — the EMERGE quote
 * stage as well as the Xero status. It was Xero-only until the owner reported
 * the stage staying on Draft while Xero read SENT, measured on QU-0540 and
 * QU-0542. So this control is no longer a Xero-only write, and nothing here may
 * word it as one.
 *
 * ⚠️ **So the dialog is a choice between WRITING and NOT WRITING, not between
 * two documents**, and every sentence on it says so. See `MARK_SENT_DIALOG_BODY`
 * in `@/lib/document-pdf`, which carries the measurement.
 *
 * ✅ **DECIDED 2026-09-24 (owner): the PLAIN download is the primary action and
 * the write is secondary.** *SUPERSEDED: "⚠️ **OPEN, and deliberately not acted
 * on here: the primary/secondary weights below were argued from the superseded
 * premise** … Whether the WRITE should still be the visually primary action is an
 * owner call, not a tidy-up."* The reason is not only that the PDF is identical:
 * **marking Sent LOCKS the quote in Xero** — the push refuses anything past Draft,
 * so later line edits are skipped and never reach the customer's copy. That is
 * how QU-0546's third line went missing. The write stays offered, with that
 * consequence stated beside it.
 *
 * ⛔ **AN INVOICE HAS NO SUCH CHOICE AND MUST NEVER GROW ONE.** Its analogous
 * Xero transition is DRAFT → AUTHORISED, which posts to the ledger and cannot be
 * undone — there is no mark-sent route on an invoice and there is not meant to
 * be. **The rule is STRUCTURAL rather than a condition somebody can forget**:
 * see {@link PdfRoutes}, whose invoice arm declares `markSent?: never`, so no
 * expression reaching the mark-sent fetcher typechecks until `tsc` has narrowed
 * the record to a quote. A guard someone can forget is not a guard.
 *
 * ## The role gate is on the WRITE alone
 *
 * ⚠️ *SUPERSEDED: "**No role gate, deliberately.** Both routes carry
 * `authenticate` and no role guard — the same guard as reading the document — so
 * every signed-in role including `viewer` may download. There is nothing here
 * for `ability` to decide, and adding a gate would hide a working capability
 * from the people most likely to want it."*
 *
 * ⚠️ *SUPERSEDED as well (2026-10-02):* the plain downloads are no longer
 * ungated, and mark-sent is no longer `requireRole(...WRITE_ROLES)` /
 * `canUpdate('quotes')`. The server now guards every route with
 * `requireGrant`: a plain download takes `<module>.export`, and mark-sent takes
 * `quotes.send`. So the control is offered on **`ability.canExport(...)`** and
 * the choice dialog on **`ability.canSend('quotes')` — never on `user.role`**
 * (read `@/lib/permissions`'s header before touching that). A reader who can
 * export but not send **downloads immediately, with no dialog**. A dialog whose only reachable option is the one they would have got
 * anyway is an affordance with a single outcome, which this app does not show —
 * the same ruling that hides the control entirely before a document is in Xero.
 *
 * ⚠️ **The gate is the optimisation and the 403 is the guarantee.** A role can
 * be taken away under a live access token, which lasts days, so the refusal is
 * still handled — `pdfRefusalMessage` falls through to the server's own sentence
 * for it, as it does for every code it does not recognise.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { api } from '@/lib/api';
import { cx } from '@/lib/cx';
import {
  canDownloadCreditNotePdf,
  canDownloadPdf,
  creditNotePdfFallbackName,
  type CreditNotePdfGate,
  MARK_SENT_CONFIRM_LABEL,
  MARK_SENT_DIALOG_BODY,
  MARK_SENT_DIALOG_TITLE,
  MARK_SENT_PLAIN_LABEL,
  pdfDocumentWord,
  type PdfDocumentKind,
  pdfRefusalMessage,
} from '@/lib/document-pdf';
import { saveBlob } from '@/lib/download';
import { useAuth } from '@/lib/use-auth';
import type { MaybeLinked } from '@/lib/xero';

/** Everything this control needs off a record — satisfied by a list row and by
 *  the full record alike, so neither page fetches anything extra. */
export interface PdfDocument extends MaybeLinked {
  id: number;
}

/**
 * How the control renders. The BEHAVIOUR is identical in all three; only the
 * affordance differs, because the three surfaces have different room and
 * different conventions.
 *
 * - `detail` — a labelled button with the refusal underneath. The detail page
 *   has the room, and it is the surface where a refusal must be readable in
 *   full.
 * - `row` — an icon-only ghost button matching the other actions in the pinned
 *   Actions column, with the refusal on a `title` and in an off-screen live
 *   region. Nothing explanatory renders as prose inside a table cell.
 * - `card` — a labelled small button, matching the sub-`md` card list.
 */
type Variant = 'detail' | 'row' | 'card';

/**
 * One fetch of one document's bytes, and the name to save them under.
 *
 * The state below is handed one of these rather than choosing between them
 * itself, which is what keeps the in-flight guard, the abort and the refusal
 * wording identical on both paths — there is one download routine and it does
 * not know, or need to know, which route it is calling.
 */
type PdfFetch = (id: number, signal: AbortSignal) => Promise<{ filename: string; blob: Blob }>;

/**
 * ⛔ **THE QUOTE-ONLY RULE, AS A TYPE.**
 *
 * `markSent?: never` on the invoice arm is the whole guard: there is no
 * expression that reads the mark-sent fetcher without first narrowing on
 * `routes.kind === 'quote'`, so a later pass cannot wire an invoice into it by
 * accident, by symmetry, or by "harmonising the two". `tsc` refuses the call
 * site rather than a comment asking somebody not to write it — the same pattern
 * as `projectId`'s absence from the document forms' `FormValues`, and as the
 * `quoteProductId` / `invoiceProductId` union on the tracker's upsert.
 *
 * ⚠️ **It is not fussiness about a branch that is obviously correct today.** The
 * invoice's counterpart transition is DRAFT → AUTHORISED, which posts to a
 * customer's ledger and which EMERGE cannot undo; a wrong answer here is not a
 * wrong render, it is a document raised in somebody's accounts.
 *
 * ✅ **MEASURED, not asserted — 2026-09-16.** Both halves were written into this
 * file and `tsc -b --force` was run: un-narrowed, `routes.markSent` is
 * `PdfFetch | undefined` and the call is **TS2345**; narrowed to the invoice arm
 * it is `undefined` and the call is **TS2345** again. The probes were then
 * removed. ⚠️ **A guard nobody has tried to defeat is a guard nobody knows the
 * shape of** — an optional property whose type happened to admit `undefined` at
 * the call site would have read identically here and refused nothing.
 */
type PdfRoutes =
  | { kind: 'quote'; plain: PdfFetch; markSent: PdfFetch }
  | { kind: 'invoice'; plain: PdfFetch; markSent?: never }
  /*
   * ⛔ **A credit note has no mark-sent route either, and for the invoice's
   * reason**: its Xero transition posts to the ledger. Same `markSent?: never`,
   * same structural refusal — see the invoice arm above.
   */
  | { kind: 'credit-note'; plain: PdfFetch; markSent?: never };

/**
 * The routes for one kind of document.
 *
 * ⚠️ **Wrapped in arrows rather than passed as bare method references**, so the
 * shape of `api` — today an object of arrow properties over a free
 * `requestBinary` — is not something this file has quietly made load-bearing.
 *
 * `reference` is read by the credit-note arm alone, for the fallback file name:
 * a credit note has no number of its own, so the name the server would use is
 * rebuilt from the same reference rather than from the id.
 */
function pdfRoutesFor(kind: PdfDocumentKind, reference: string): PdfRoutes {
  switch (kind) {
    case 'quote':
      return {
        kind: 'quote',
        plain: (id, signal) => api.downloadQuotePdf(id, signal),
        markSent: (id, signal) => api.downloadQuotePdfAsSent(id, signal),
      };
    case 'invoice':
      return {
        kind: 'invoice',
        plain: (id, signal) => api.downloadInvoicePdf(id, signal),
      };
    case 'credit-note':
      return {
        kind: 'credit-note',
        plain: (id, signal) =>
          api.downloadCreditNotePdf(id, creditNotePdfFallbackName(reference), signal),
      };
    default: {
      const unreachable: never = kind;
      return unreachable;
    }
  }
}

function DownloadIcon() {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4"
      aria-hidden="true"
    >
      <path d="M10 3v9m0 0 3.5-3.5M10 12 6.5 8.5" />
      <path d="M3.5 13.5v1A2.5 2.5 0 0 0 6 17h8a2.5 2.5 0 0 0 2.5-2.5v-1" />
    </svg>
  );
}

/** A credit note, as far as this control reads one — see `canDownloadCreditNotePdf`. */
export interface CreditNotePdfDocument extends PdfDocument, CreditNotePdfGate {}

/**
 * ⚠️ **A discriminated union, because the two arms are gated differently.** A
 * quote or an invoice is decided by its `xero` envelope alone; a credit note's
 * read carries no envelope yet, so its gate reads two more fields, and only the
 * credit-note arm is required to supply them.
 */
type DownloadPdfButtonProps = DownloadPdfButtonCommon &
  (
    | {
        kind: 'quote' | 'invoice';
        /** The record itself — its `xero` envelope is what decides visibility. */
        document: PdfDocument;
      }
    | {
        kind: 'credit-note';
        document: CreditNotePdfDocument;
      }
  );

/** Whether the control is offered at all, per arm. */
function offeredFor(props: DownloadPdfButtonProps): boolean {
  return props.kind === 'credit-note'
    ? canDownloadCreditNotePdf(props.document)
    : canDownloadPdf(props.document);
}

interface DownloadPdfButtonCommon {
  /**
   * How this document is named to a person — the accessible label, and the
   * record named in the mark-sent dialog.
   *
   * ⚠️ *SUPERSEDED: "for the accessible label only."* The dialog names the
   * record too, because a confirmation that does not is unanswerable from a
   * table row: the trigger is an icon in one cell of twenty, and the dialog is
   * portalled away from it.
   *
   * ⚠️ **Never used as a file name**, and that half is untouched. The saved file
   * is named from the server's own `Content-Disposition`, which carries the Xero
   * number; deriving one here would put a stale reference on a document Xero may
   * have renumbered.
   */
  reference: string;
  variant: Variant;
  className?: string;
}

export function DownloadPdfButton(props: DownloadPdfButtonProps) {
  const { kind, document: record, reference, variant, className } = props;
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /*
   * Open only ever while the reader is being asked which of the two downloads
   * they want, which is a state that exists on a quote and for a writer alone —
   * see the header. It is held here rather than by the caller because it belongs
   * to one record, and four pages holding it would be four chances for one
   * row's dialog to outlive its row.
   */
  const [choiceOpen, setChoiceOpen] = useState(false);

  /*
   * ⚠️ **A hook, so it runs above the hide** — and gating on the ability rather
   * than the role is the house rule, not a preference: `/permissions/me` and the
   * role disagree in both directions, and `ability` is the one place they are
   * reconciled.
   */
  const { ability } = useAuth();
  /*
   * 2026-10-02 (owner): a PDF download is `<module>.export`, and mark-sent is
   * `quotes.send`. *SUPERSEDED: the plain download ungated, and mark-sent on
   * `canUpdate('quotes')`.*
   */
  const canMarkSent = ability.canSend('quotes');
  const canDownload = ability.canExport(
    kind === 'quote' ? 'quotes' : kind === 'invoice' ? 'invoices' : 'credit-notes',
  );

  /*
   * The in-flight controller, so an unmount aborts the request rather than
   * leaving it to resolve into a component that is gone — and, more to the
   * point, rather than writing a file to disk on behalf of a page the reader has
   * already navigated away from.
   */
  const abortRef = useRef<AbortController | null>(null);
  useEffect(() => () => abortRef.current?.abort(), []);

  /*
   * Where focus lands when the choice opens.
   *
   * ⛔ **Cancel, copied from `ConfirmDialog` rather than reinvented**, and it is
   * the answer to this dialog being *destructive-ish*: one of the two actions
   * writes to somebody else's accounting system and EMERGE cannot undo it, so
   * the option a stray Enter or Space hits must be the one that does nothing.
   * That is a separate decision from which action is emphasised — see the
   * footer, where (since 2026-09-24) the plain download is the primary.
   */
  const cancelRef = useRef<HTMLButtonElement>(null);

  /*
   * Reset when the row changes identity.
   *
   * ⚠️ **Adjusted DURING RENDER, not in an effect** — React's own documented
   * pattern for resetting state on a prop change: the in-progress output is
   * discarded and re-rendered immediately, so nothing is committed and no second
   * paint happens. An effect would paint the previous record's refusal for a
   * frame, and on a table whose rows recycle as pages change that frame is
   * visible — one quote's error under another quote's number.
   *
   * The link status is part of the identity because a record refreshed into a
   * live link must not keep a "not in Xero" sentence from before the push.
   */
  const identity = `${record.id}:${record.xero?.status ?? 'none'}:${record.xero?.id ?? 'none'}`;
  const [renderedFor, setRenderedFor] = useState(identity);
  if (renderedFor !== identity) {
    setRenderedFor(identity);
    setError(null);
    /*
     * ⛔ **The dialog closes with the identity, and this line is the difference
     * between a stale sentence and a WRITE against the wrong record.** The
     * refusal above it is merely wrong on screen; a dialog left standing as a
     * row recycles is a confirmation raised about one quote, answered about
     * another, and the answer marks that other quote Sent in the accountant's
     * Xero. Nothing would fail: the request succeeds and returns a PDF.
     */
    setChoiceOpen(false);
  }

  const download = useCallback(
    async (fetchPdf: PdfFetch) => {
      /*
       * Checked here as well as expressed through `loading`: the button being
       * disabled is the optimisation, this is the guarantee.
       *
       * 🔴 **AND THE COST OF A DOUBLE-FIRE IS NO LONGER WHAT THIS COMMENT SAID.**
       * *SUPERSEDED: "A double-fire writes the same file to disk twice, which is
       * untidy rather than dangerous."* True of the two plain reads, and false of
       * mark-sent: that one is a **second POST that reaches the accountant's
       * Xero**. It is idempotent, so the second one is survivable rather than
       * harmful — but "survivable" is a property of today's server, and the
       * guard is what stops this control depending on it.
       * ⚠️ *SUPERSEDED: "Both paths also still spend against a 120/60s per-IP
       * bucket shared across the office."* It is **12,000/60s** (corrected
       * 2026-09-17), so the bucket was never what made this guard necessary —
       * the second POST reaching Xero is.
       */
      if (downloading) return;

      const controller = new AbortController();
      abortRef.current?.abort();
      abortRef.current = controller;

      setDownloading(true);
      setError(null);

      try {
        const { filename, blob } = await fetchPdf(record.id, controller.signal);

        if (controller.signal.aborted) return;

        /*
         * The server's own name, sanitised but never replaced — see `saveBlob`.
         * It carries the Xero number, which is the reference the customer's
         * finance team matches against; `requestBinary` falls back to the
         * neutral `quote-{id}` / `invoice-{id}` only when the header is
         * unreadable. ⚠️ **Both quote routes fall back to the same name**, so
         * nothing downstream can tell which one produced the file — and nothing
         * needs to.
         */
        saveBlob(filename, blob);
      } catch (caught) {
        if (controller.signal.aborted) return;
        /*
         * One wording function for every refusal on every path — the two 409s,
         * mark-sent's third code, the 403 a reader whose role moved under a live
         * token meets, and anything else. An unrecognised code falls through to
         * the server's own sentence: unknown means "newer than this branch",
         * never "not a refusal".
         *
         * ⚠️ **`kind` is still the right argument even on the mark-sent path**,
         * because it words the document, not the route.
         */
        setError(pdfRefusalMessage(caught, kind));
      } finally {
        /*
         * Not guarded on `aborted`: an aborted request means this control is
         * being torn down or superseded, and in the supersede case the state
         * must still settle or the button spins for ever with nothing logged and
         * nothing to retry. Setting state on an unmounted component is a no-op
         * in React 19.
         */
        setDownloading(false);
        if (abortRef.current === controller) abortRef.current = null;
      }
    },
    [downloading, kind, record.id],
  );

  /*
   * ⚠️ THE HIDE. Every hook above runs first, unconditionally — an early return
   * placed before them would change the hook count between renders the moment a
   * record gained a link, which React treats as an error rather than a re-render.
   */
  if (!offeredFor(props)) return null;
  if (!canDownload) return null;

  const label = 'Download PDF';
  const accessibleLabel = `${label} for ${pdfDocumentWord(kind)} ${reference}`;

  const routes = pdfRoutesFor(kind, reference);

  /** Chosen, so the question is answered and gone before the request starts. */
  const choose = (fetchPdf: PdfFetch) => {
    setChoiceOpen(false);
    void download(fetchPdf);
  };

  /*
   * ⚠️ **ONE expression decides BOTH whether the dialog exists and what the
   * trigger does**, and it is deliberately a node rather than a boolean beside
   * a separate render. Two spellings of "does this reader get the choice" would
   * drift, and both directions fail silently: a trigger that opens a dialog
   * which is not rendered does *nothing at all* on click, and a dialog rendered
   * for a reader the trigger never opens is dead markup with a write behind it.
   *
   * ⛔ **`routes.kind === 'quote'` is doing real work here, not restating
   * `kind`.** It is the narrowing that makes `routes.markSent` readable at all —
   * remove it and the file stops compiling, which is the point of the union.
   */
  const choiceDialog =
    canMarkSent && routes.kind === 'quote' ? (
      <Modal
        open={choiceOpen}
        onClose={() => setChoiceOpen(false)}
        title={MARK_SENT_DIALOG_TITLE}
        /*
         * The record, named. The supplied copy is static and cannot carry a
         * reference, and a confirmation raised from an icon in a table cell is
         * unanswerable without one — see the `reference` prop.
         *
         * ⚠️ **The word is written out rather than derived from `kind`**: the
         * branch is already narrowed to a quote, so a ternary here would be a
         * second arm that provably cannot render.
         */
        description={`Quote ${reference}`}
        size="sm"
        /*
         * A mis-click on the backdrop must not read as "no" any more than it
         * reads as "yes": this choice costs the reader a decision they came here
         * to make. Escape still closes, as it does on every dialog in the app.
         */
        dismissOnBackdrop={false}
        initialFocusRef={cancelRef}
        footer={
          <>
            {/*
              ⚠️ **`ghost`, where `ConfirmDialog` would use `secondary`** — and
              the deviation is the ranking rather than a stray. Three actions
              need three weights, and Cancel is the only one of them that does
              NOTHING: both of the others are complete answers to the click that
              opened this. `secondary` here would put the escape hatch above a
              real download in the visual order, which is the wrong way round.
              Precedent: `CompanyProjectLinesDialog`, which ranks its footer the
              same way.
            */}
            <Button ref={cancelRef} variant="ghost" onClick={() => setChoiceOpen(false)}>
              Cancel
            </Button>
            {/*
              ✅ **SECONDARY — owner's decision 2026-09-24.** *SUPERSEDED: "🔴
              **PRIMARY ON A SUPERSEDED ARGUMENT** … **The weight is left as it
              shipped rather than changed on my own authority**, because which
              action a dialog emphasises is an owner call."* The owner made it:
              marking Sent LOCKS the quote in Xero, so later line edits are
              skipped and never reach the customer's PDF (QU-0546). The body text
              says so; the weight follows it.
              Not `danger`, and that half stands: this moves a status and posts
              nothing — red here would be crying wolf on the screen that also has
              to carry real refusals.
            */}
            <Button variant="secondary" onClick={() => choose(routes.markSent)}>
              {MARK_SENT_CONFIRM_LABEL}
            </Button>
            {/*
              ✅ **PRIMARY since 2026-09-24 (owner).** The unchanged,
              always-available path — the one every reader holding
              `quotes.export` gets without being asked, the recovery when the write is refused, and the identical
              document. Placed last so it takes the primary position in the
              footer.
              ⚠️ **Emphasis is not the same decision as what a stray keystroke
              hits**: focus still starts on Cancel above. A download writes
              nothing, but the two decisions stay separate so a future reorder
              cannot move initial focus by accident.
            */}
            <Button variant="primary" onClick={() => choose(routes.plain)}>
              {MARK_SENT_PLAIN_LABEL}
            </Button>
          </>
        }
      >
        <p className="m-0 text-sm text-fg-secondary">{MARK_SENT_DIALOG_BODY}</p>
      </Modal>
    ) : null;

  /*
   * The trigger. A reader who is not being offered the choice downloads
   * immediately — there is no dialog for them to dismiss, because its only
   * reachable option would be the one they just asked for.
   */
  const onTrigger = () => {
    if (choiceDialog !== null) {
      setChoiceOpen(true);
      return;
    }
    void download(routes.plain);
  };

  if (variant === 'row') {
    return (
      <>
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          loading={downloading}
          leadingIcon={<DownloadIcon />}
          aria-label={accessibleLabel}
          title={error ?? label}
          onClick={onTrigger}
        />
        <span role="status" className="sr-only">
          {error ?? ''}
        </span>
        {/*
          ⚠️ **The dialog is fine in this variant and inline prose still is
          not.** Nothing explanatory may render inside a table cell — the
          refusal above is still routed to `title` and the live region for
          exactly that reason — but a `Modal` is portalled to the body, so it
          is not cell content at any width and cannot widen the column.
        */}
        {choiceDialog}
      </>
    );
  }

  return (
    <div className={cx('grid gap-1.5', className)}>
      <div>
        <Button
          variant="secondary"
          size={variant === 'card' ? 'sm' : 'md'}
          loading={downloading}
          leadingIcon={<DownloadIcon />}
          aria-label={accessibleLabel}
          onClick={onTrigger}
        >
          {label}
        </Button>
      </div>

      {choiceDialog}

      {/*
        A refusal, and nothing else. There is deliberately no success line: the
        browser owns what happens after the click, so a download blocked by an
        extension looks identical from here and a "Saved." would be a claim this
        component cannot support.

        ⚠️ **A refusal from either path lands HERE, not in the dialog, because
        the dialog is gone by the time the request is made.** That is deliberate:
        a refused mark-sent may or may not have moved the quote before it failed,
        so a Retry offered inside the dialog would be a control whose effect
        nobody can state. Closing first leaves the ordinary recovery — press
        Download PDF again and answer the question afresh, which is safe because
        the route is idempotent.
      */}
      {error !== null && (
        <p role="alert" className="m-0 text-xs text-danger [overflow-wrap:anywhere]">
          {error}
        </p>
      )}
    </div>
  );
}
